/**
 * Trunnion AI - Trigger Node
 * Workflow entry point.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Zap, Clock, Webhook, Radio } from 'lucide-react';
import type { TriggerNodeData } from '@/types';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';

const triggerIcons = {
  manual: Zap,
  scheduled: Clock,
  webhook: Webhook,
  event: Radio,
};

export const TriggerNode = memo(function TriggerNode({
  id,
  data,
  selected,
}: NodeProps & { data: TriggerNodeData }) {
  const setSelectedNodeId = useWorkflowDesignerStore((s) => s.setSelectedNodeId);
  const TriggerIcon = triggerIcons[data.triggerType];

  return (
    <div
      className={`min-w-50 rounded-xl border-2 bg-trunnion-slate-800 shadow-lg transition-all ${
        selected
          ? 'border-green-500 shadow-[0_0_15px_rgba(34,197,94,0.3)]'
          : 'border-green-500/40 hover:border-green-500/60'
      }`}
      onClick={() => setSelectedNodeId(id)}
    >
      <div className="flex items-center justify-center gap-2 rounded-t-[10px] bg-green-500/10 px-4 py-3">
        <TriggerIcon className="h-5 w-5 text-green-400" />
        <span className="text-sm font-semibold text-white">
          {data.label || 'Trigger'}
        </span>
      </div>
      <div className="px-3 py-2 text-center">
        <span className="text-xs text-trunnion-slate-400 capitalize">
          {data.triggerType} trigger
        </span>
        {data.schedule && (
          <p className="mt-0.5 text-xs text-trunnion-slate-300 font-mono">
            {data.schedule}
          </p>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-trunnion-slate-800 !border-green-500"
      />
    </div>
  );
});
